import { Link } from "react-router-dom";
import { Download, FileText, ShieldCheck } from "lucide-react";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button, buttonVariants } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/feedback";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/data-display/page-header";
import { KVList } from "@/components/data-display/kv-list";
import { UnavailableValue } from "@/components/data-display/unavailable-value";
import { WipHint, WIP_ACTION_HINT } from "@/components/ui/wip-hint";

/**
 * Compliance Evidence Packet (/evidence/packet) — WIP layout port (ALL-5825).
 *
 * Structure, spacing and tokens are ported from the designer's
 * `.design-ref/prototype/features/oversight/evidence.tsx` `EvidencePacket`:
 * breadcrumb → page header → a 320px packet panel beside a tabbed detail panel
 * → a sealed chain-of-custody panel.
 *
 * There is no compliance-packet backend in this environment
 * (context/page_pipeline/BACKEND_GAPS.md), so this is a layout-only port:
 *
 *  - no prototype sample data is copied in: every packet field renders the
 *    shared `UnavailableValue` marker, and the timeline, receipt, screening and
 *    custody surfaces each render a designed empty state;
 *  - the designer's `Open timeline` action needs a packet id this environment
 *    cannot produce, so it renders disabled and says why instead of pretending
 *    to run. The `Export` action is a real link to `/evidence/export` (a route
 *    that exists in App.tsx);
 *  - controls that would need the backend render `disabled` and carry
 *    `WIP_ACTION_HINT` through both `title` and `aria-describedby`.
 *
 * It replaced a `DeferredPage` (ALL-5709/ALL-5778); the not-available wording
 * survives as the plain sentence under the title. Removing the WIP hint is part
 * of the PR that wires the real API.
 */

const WIP_DESCRIPTION_ID = "evidence-packet-wip-description";

/** Panel chrome ported from the designer's `Panel` (features/oversight/oversight-ui.tsx). */
function Panel({
  title,
  children,
  bodyClassName,
}: {
  title?: React.ReactNode;
  children: React.ReactNode;
  bodyClassName?: string;
}) {
  return (
    <section className="mb-4 overflow-hidden rounded-lg border border-line bg-surface">
      {title && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-line px-4 py-2">
          <h3 className="text-sm font-bold text-ink">{title}</h3>
        </header>
      )}
      <div className={bodyClassName}>{children}</div>
    </section>
  );
}

export default function EvidencePacketPage() {
  return (
    <div>
      <Breadcrumb items={[{ label: "Evidence", href: "/evidence" }, { label: "Compliance Packet" }]} />

      <PageHeader
        title="Compliance Evidence Packet"
        subtitle="Immutable, sealed record of every material action across the platform."
        actions={
          <>
            <WipHint />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled
              title={WIP_ACTION_HINT}
              aria-describedby={WIP_DESCRIPTION_ID}
            >
              Open timeline
            </Button>
            <Link to="/evidence/export" className={buttonVariants({ variant: "primary", size: "sm" })}>
              <Download aria-hidden className="size-4" /> Export
            </Link>
          </>
        }
      />
      <p className="-mt-3 mb-5 text-sm text-muted">
        Compliance packet has no backend in this environment yet, so nothing here reflects a real packet.
      </p>
      <p id={WIP_DESCRIPTION_ID} className="sr-only">
        {WIP_ACTION_HINT}
      </p>

      <div className="grid gap-5 lg:grid-cols-[320px_minmax(0,1fr)]">
        <div>
          <Panel
            title={
              <span className="flex items-center gap-2">
                <FileText aria-hidden className="size-4 text-primary" />Packet
              </span>
            }
            bodyClassName="px-5 py-4"
          >
            <KVList
              rows={[
                { k: "Packet", v: <UnavailableValue /> },
                { k: "Type", v: <UnavailableValue /> },
                { k: "Actor", v: <UnavailableValue /> },
                { k: "Created", v: <UnavailableValue /> },
                { k: "Sealed", v: <UnavailableValue /> },
                { k: "Transfer", v: <UnavailableValue /> },
                { k: "Approval", v: <UnavailableValue /> },
              ]}
            />
          </Panel>
        </div>

        <div>
          <Panel bodyClassName="px-5 pb-5 pt-1">
            <Tabs defaultValue="timeline">
              <TabsList className="mb-4">
                <TabsTrigger value="timeline">Timeline</TabsTrigger>
                <TabsTrigger value="receipt">Receipt</TabsTrigger>
                <TabsTrigger value="screening">Screening</TabsTrigger>
              </TabsList>
              <TabsContent value="timeline">
                <EmptyState
                  title="No timeline events"
                  description="Packet events are not published in this environment yet, so there is no chain of custody to show."
                />
              </TabsContent>
              <TabsContent value="receipt">
                <EmptyState
                  title="No policy evaluation receipt"
                  description="Policy and risk receipts are not published in this environment yet."
                />
              </TabsContent>
              <TabsContent value="screening">
                <EmptyState
                  title="No screening result"
                  description="Sanctions and screening results are not published in this environment yet."
                />
              </TabsContent>
            </Tabs>
          </Panel>

          <Panel title="Sealed chain-of-custody" bodyClassName="px-5 py-4">
            <EmptyState
              icon={<ShieldCheck aria-hidden className="size-5 text-muted" />}
              title="No chain-of-custody events"
              description="Sealed custody events are not published in this environment yet, so no packet can be verified from this page."
            />
          </Panel>
        </div>
      </div>
    </div>
  );
}
